import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Calendar, Gift, User, Cake, MessageCircle, Send, Trash2, X, Heart, Sparkles, UserX } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { useGamification } from '../contexts/GamificationContext';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface Aniversariante {
  nome: string;
  cpf?: string;
  dataNascimento: string;
  dataISO: string;
  dia: number;
  mesNumero: number;
  foto?: string | null;
}

interface Comentario {
  id: string;
  usuario_id: string;
  usuario_nome: string;
  mensagem: string;
  created_at: string;
}

export const Aniversariantes: React.FC = () => {
  const { user } = useAuth();
  const { addActivity } = useGamification();

  const [aniversariantes, setAniversariantes] = useState<Aniversariante[]>([]);
  const [aniversariantesOcultos, setAniversariantesOcultos] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string>('');

  const [comentariosPorPessoa, setComentariosPorPessoa] = useState<Record<string, Comentario[]>>({});
  const [pessoaSelecionada, setPessoaSelecionada] = useState<Aniversariante | null>(null);
  const [novoComentario, setNovoComentario] = useState('');
  const [enviandoComentario, setEnviandoComentario] = useState(false);

  const mesAtual = new Date().getMonth() + 1;
  const anoAtual = new Date().getFullYear();
  const nomeDoMes = format(new Date(), 'MMMM', { locale: ptBR });

  const loadComentarios = useCallback(async (cpf: string) => {
    try {
      console.log('[ANIVERSARIANTES] Carregando comentários para CPF:', cpf, 'Mês:', mesAtual);

      const { data, error } = await supabase
        .from('aniversariantes_comentarios')
        .select('*')
        .eq('aniversariante_cpf', cpf)
        .eq('mes_aniversario', mesAtual)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[ANIVERSARIANTES] Erro no SELECT:', error);
        throw error;
      }

      console.log('[ANIVERSARIANTES] Comentários carregados:', data?.length || 0);

      const comentarios: Comentario[] = (data || []).map((c: any) => ({
        id: c.id,
        usuario_id: c.usuario_id,
        usuario_nome: c.usuario_nome,
        mensagem: c.mensagem,
        created_at: c.created_at,
      }));

      setComentariosPorPessoa(prev => ({ ...prev, [cpf]: comentarios }));
    } catch (error: any) {
      console.error('[ANIVERSARIANTES] Erro detalhado ao carregar comentários:', error);
      toast.error('Erro ao carregar comentários');
    }
  }, [mesAtual]);

  const loadAniversariantesOcultos = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('aniversariantes_ocultos')
        .select('cpf_ou_nome');

      if (error) throw error;

      const ocultosSet = new Set<string>();
      data?.forEach((item: any) => {
        ocultosSet.add(item.cpf_ou_nome);
      });

      setAniversariantesOcultos(ocultosSet);
    } catch (error) {
      console.error('Erro ao carregar aniversariantes ocultos:', error);
    }
  }, []);

  useEffect(() => {
    const loadAniversariantes = async () => {
      try {
        await loadAniversariantesOcultos();

        const response = await fetch('/dados/aniversariantes.json');
        if (!response.ok) {
          throw new Error('Não foi possível carregar os dados dos aniversariantes');
        }
        const data = await response.json();

        const aniversariantesDoMes = data
          .filter((pessoa: Aniversariante) => pessoa.mesNumero === mesAtual)
          .sort((a: Aniversariante, b: Aniversariante) => a.dia - b.dia);

        const { data: contatos } = await supabase
          .from('contatos')
          .select('nome, usuarios!contatos_usuario_id_fkey(picture)')
          .eq('ativo', true);

        const fotoMap = new Map<string, string>();
        contatos?.forEach((c: any) => {
          if (c.usuarios?.picture) {
            fotoMap.set(c.nome?.toUpperCase()?.trim(), c.usuarios.picture);
          }
        });

        const comFotos = aniversariantesDoMes.map((pessoa: Aniversariante) => ({
          ...pessoa,
          foto: fotoMap.get(pessoa.nome?.toUpperCase()?.trim()) || null,
        }));

        setAniversariantes(comFotos);

        comFotos.forEach((pessoa: Aniversariante) => {
          const cpf = pessoa.cpf || pessoa.nome;
          loadComentarios(cpf);
        });
      } catch (error) {
        console.error('Erro ao carregar aniversariantes:', error);
        setErro('Erro ao carregar dados dos aniversariantes');
      } finally {
        setLoading(false);
      }
    };

    loadAniversariantes();
  }, [mesAtual, loadComentarios, loadAniversariantesOcultos]);

  const handleAbrirModal = (pessoa: Aniversariante) => {
    setPessoaSelecionada(pessoa);
    const cpf = pessoa.cpf || pessoa.nome;
    if (!comentariosPorPessoa[cpf]) {
      loadComentarios(cpf);
    }
  };

  const handleFecharModal = () => {
    setPessoaSelecionada(null);
    setNovoComentario('');
  };

  const handleEnviarComentario = async () => {
    if (!pessoaSelecionada || !novoComentario.trim() || !user?.dbId) {
      console.error('[ANIVERSARIANTES] Dados inválidos:', {
        pessoaSelecionada: !!pessoaSelecionada,
        mensagem: !!novoComentario.trim(),
        userId: user?.dbId
      });
      return;
    }

    const cpf = pessoaSelecionada.cpf || pessoaSelecionada.nome;

    console.log('[ANIVERSARIANTES] Enviando comentário:', {
      cpf,
      nome: pessoaSelecionada.nome,
      usuario_id: user.dbId,
      usuario_nome: user.name,
      mes: mesAtual
    });

    setEnviandoComentario(true);
    try {
      const { data: comentariosExistentes, error: checkError } = await supabase
        .from('aniversariantes_comentarios')
        .select('id')
        .eq('aniversariante_cpf', cpf)
        .eq('usuario_id', user.dbId)
        .eq('mes_aniversario', mesAtual);

      if (checkError) {
        console.error('[ANIVERSARIANTES] Erro ao verificar comentários existentes:', checkError);
        throw checkError;
      }

      const jaComentou = comentariosExistentes && comentariosExistentes.length > 0;

      const { error } = await supabase
        .from('aniversariantes_comentarios')
        .insert({
          aniversariante_cpf: cpf,
          aniversariante_nome: pessoaSelecionada.nome,
          usuario_id: user.dbId,
          usuario_nome: user.name,
          mensagem: novoComentario.trim(),
          mes_aniversario: mesAtual,
        });

      if (error) {
        console.error('[ANIVERSARIANTES] Erro no INSERT:', error);
        throw error;
      }

      console.log('[ANIVERSARIANTES] Comentário enviado com sucesso');

      if (!jaComentou) {
        addActivity('birthday_comment', `Parabenizou ${pessoaSelecionada.nome}`, {
          birthday_person: pessoaSelecionada.nome
        });
        toast.success(`Mensagem enviada! +2 pontos`);
      } else {
        toast.success(`Mensagem enviada!`);
      }

      setNovoComentario('');
      await loadComentarios(cpf);
    } catch (error: any) {
      console.error('[ANIVERSARIANTES] Erro detalhado ao enviar comentário:', error);
      toast.error('Erro ao enviar mensagem: ' + (error.message || 'Tente novamente'));
    } finally {
      setEnviandoComentario(false);
    }
  };

  const handleDeletarComentario = async (comentarioId: string, cpf: string) => {
    try {
      const { error } = await supabase
        .from('aniversariantes_comentarios')
        .delete()
        .eq('id', comentarioId);

      if (error) throw error;

      toast.success('Comentário excluído!');
      await loadComentarios(cpf);
    } catch (error: any) {
      console.error('[ANIVERSARIANTES] Erro ao deletar comentário:', error);
      toast.error('Erro ao excluir comentário');
    }
  };

  const handleOcultarAniversariante = async (pessoa: Aniversariante) => {
    if (!user?.dbId) {
      toast.error('Usuário não autenticado');
      return;
    }

    if (user.role !== 'admin' && user.setor !== 'RH') {
      toast.error('Apenas administradores e RH podem excluir aniversariantes');
      return;
    }

    const confirmacao = window.confirm(
      `Tem certeza que deseja excluir ${pessoa.nome} da lista de aniversariantes?\n\nEsta ação irá ocultar o aniversariante de todos os usuários.`
    );

    if (!confirmacao) return;

    try {
      const cpf = pessoa.cpf || pessoa.nome;

      const { error } = await supabase
        .from('aniversariantes_ocultos')
        .insert({
          cpf_ou_nome: cpf,
          nome: pessoa.nome,
          motivo: 'Desligado da empresa',
          excluido_por: user.dbId,
        });

      if (error) throw error;

      toast.success(`${pessoa.nome} foi excluído da lista de aniversariantes`);
      await loadAniversariantesOcultos();
    } catch (error: any) {
      console.error('[ANIVERSARIANTES] Erro ao ocultar aniversariante:', error);
      toast.error('Erro ao excluir aniversariante');
    }
  };

  const formatarDataHora = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch {
      return '—';
    }
  };

  const calcularIdade = (dataISO: string): number => {
    const nascimento = new Date(dataISO);
    const hoje = new Date();
    let idade = hoje.getFullYear() - nascimento.getFullYear();
    
    const mesAtualCompleto = hoje.getMonth();
    const diaAtualCompleto = hoje.getDate();
    const mesNascimento = nascimento.getMonth();
    const diaNascimento = nascimento.getDate();
    
    if (mesAtualCompleto < mesNascimento || (mesAtualCompleto === mesNascimento && diaAtualCompleto < diaNascimento)) {
      idade--;
    }
    
    return idade;
  };

  const jaFezAniversario = (dia: number): boolean => {
    const hoje = new Date();
    return hoje.getDate() >= dia;
  };

  const isAniversarioHoje = (dia: number): boolean => {
    const hoje = new Date();
    return hoje.getDate() === dia && hoje.getMonth() + 1 === mesAtual;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  const totalVisiveis = aniversariantes.filter(p => !aniversariantesOcultos.has(p.cpf || p.nome)).length;

  return (
    <Layout>
      <div className="space-y-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <h1 className="font-display text-display-lg text-primary mb-2 capitalize">
              Aniversariantes de {nomeDoMes}
            </h1>
            <p className="text-body-lg text-secondary max-w-2xl">
              Celebre a vida dos nossos colaboradores que fazem parte da jornada GCF.
            </p>
          </div>
          <div className="flex items-center gap-4 bg-primary/5 border border-primary/20 rounded-xl px-6 py-4">
            <div className="bg-primary text-white w-12 h-12 rounded-lg flex items-center justify-center">
              <Cake className="w-6 h-6" />
            </div>
            <div>
              <div className="text-3xl font-bold text-primary leading-tight">{totalVisiveis}</div>
              <div className="text-label-md font-semibold uppercase tracking-wider text-secondary">Colaboradores</div>
            </div>
          </div>
        </div>

        {erro ? (
          <div className="bg-error-container border border-error/20 rounded-xl p-4">
            <div className="text-error">{erro}</div>
          </div>
        ) : aniversariantes.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-outline-variant p-12 text-center">
            <Cake className="w-16 h-16 text-secondary/40 mx-auto mb-4" />
            <h3 className="text-headline-md text-on-surface mb-2">
              Nenhum aniversariante este mes
            </h3>
            <p className="text-secondary">
              Nao ha aniversarios registrados para {nomeDoMes}.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {aniversariantes
              .filter((pessoa) => {
                const cpf = pessoa.cpf || pessoa.nome;
                return !aniversariantesOcultos.has(cpf);
              })
              .map((pessoa, index) => {
                const idade = calcularIdade(pessoa.dataISO);
                const jaComemorou = jaFezAniversario(pessoa.dia);
                const eHoje = isAniversarioHoje(pessoa.dia);
                const cpf = pessoa.cpf || pessoa.nome;
                const comentarios = comentariosPorPessoa[cpf] || [];
                const podeExcluir = user?.role === 'admin' || user?.setor === 'RH';

                return (
                  <div
                    key={index}
                    className={`bg-white dark:bg-gray-800 border rounded-xl overflow-hidden transition-all duration-300 flex flex-col relative group hover:shadow-lg hover:-translate-y-0.5 ${
                      eHoje
                        ? 'border-primary/30 ring-1 ring-primary/20'
                        : 'border-outline-variant'
                    }`}
                  >
                    {eHoje && <div className="absolute top-0 left-0 w-full h-1 bg-primary" />}

                    {/* Date badge */}
                    <div className={`absolute top-4 right-4 px-3 py-1 rounded-full flex items-center gap-1 ${
                      eHoje ? 'bg-primary text-white' : 'bg-primary/10 text-primary'
                    }`}>
                      <Calendar className="w-3.5 h-3.5" />
                      <span className="text-label-md font-bold">{String(pessoa.dia).padStart(2, '0')}/{String(mesAtual).padStart(2, '0')}</span>
                    </div>

                    <div className="p-6 flex gap-4 items-start cursor-pointer" onClick={() => handleAbrirModal(pessoa)}>
                      <div className="relative">
                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ring-4 ring-white dark:ring-gray-700 shadow-md overflow-hidden ${
                          eHoje ? 'bg-primary' : 'bg-secondary-container'
                        }`}>
                          {pessoa.foto ? (
                            <img src={pessoa.foto} alt={pessoa.nome} className="w-full h-full object-cover" />
                          ) : (
                            <User className={`w-8 h-8 ${eHoje ? 'text-white' : 'text-primary'}`} />
                          )}
                        </div>
                        {eHoje && (
                          <div className="absolute -bottom-1 -right-1 bg-primary text-white w-6 h-6 rounded-full flex items-center justify-center border-2 border-white dark:border-gray-700">
                            <Cake className="w-3 h-3" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-display text-headline-md text-on-surface leading-tight mb-1 group-hover:text-primary transition-colors pr-16">{pessoa.nome}</h3>
                        <div className="space-y-1 mt-3">
                          <div className="flex justify-between text-body-md">
                            <span className="text-secondary">Nascimento:</span>
                            <span className="font-medium">{pessoa.dataNascimento}</span>
                          </div>
                          <div className="flex justify-between text-body-md">
                            <span className="text-secondary">Idade:</span>
                            <span className="font-medium text-primary">
                              {eHoje ? `${idade} anos` : jaComemorou ? `${idade} anos` : `${idade + 1} anos`}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-auto px-6 pb-6 pt-2">
                      <div className="flex items-center justify-between py-3 border-t border-outline-variant mb-4">
                        <div className="flex items-center gap-2 text-secondary text-body-md">
                          <MessageCircle className="w-4 h-4" />
                          <span>{comentarios.length} mensagens</span>
                        </div>
                        {podeExcluir && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOcultarAniversariante(pessoa);
                            }}
                            className="p-1.5 text-error/60 hover:text-error hover:bg-error-container/30 rounded-lg transition-colors"
                            title="Excluir aniversariante"
                          >
                            <UserX className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <button
                        onClick={() => handleAbrirModal(pessoa)}
                        className="w-full py-3 bg-primary text-white rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-primary-dark transition-colors shadow-sm active:scale-95 duration-150"
                      >
                        <Send className="w-4 h-4" />
                        Enviar Parabens
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {/* Modal */}
        {pessoaSelecionada && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden border border-outline-variant">
              {(() => {
                const eHoje = isAniversarioHoje(pessoaSelecionada.dia);
                const jaComemorou = jaFezAniversario(pessoaSelecionada.dia);
                const idade = calcularIdade(pessoaSelecionada.dataISO);
                const cpf = pessoaSelecionada.cpf || pessoaSelecionada.nome;
                const comentarios = comentariosPorPessoa[cpf] || [];

                return (
                  <>
                    <div className="p-6 bg-primary text-white">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center overflow-hidden">
                            {pessoaSelecionada.foto ? (
                              <img src={pessoaSelecionada.foto} alt={pessoaSelecionada.nome} className="w-full h-full object-cover" />
                            ) : (
                              <Cake className="w-7 h-7 text-white" />
                            )}
                          </div>
                          <div>
                            <h2 className="text-xl font-bold">{pessoaSelecionada.nome}</h2>
                            <div className="flex items-center space-x-2 mt-1 opacity-90">
                              <Calendar className="w-4 h-4" />
                              <span className="text-sm">{pessoaSelecionada.dia}/{mesAtual} - {eHoje ? `${idade} anos hoje!` : `${jaComemorou ? idade : idade + 1} anos`}</span>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={handleFecharModal}
                          className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                        >
                          <X className="w-5 h-5 text-white" />
                        </button>
                      </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-6 bg-surface-container-low">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-on-surface flex items-center space-x-2">
                          <Heart className="w-5 h-5 text-primary" />
                          <span>Mensagens de Parabens</span>
                        </h3>
                        <span className="text-sm text-secondary">{comentarios.length} mensagens</span>
                      </div>

                      {comentarios.length > 0 ? (
                        <div className="space-y-3">
                          {comentarios.map((comentario) => (
                            <div
                              key={comentario.id}
                              className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-outline-variant hover:shadow-md transition-shadow"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-2">
                                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                                      <span className="text-white text-xs font-bold">
                                        {comentario.usuario_nome.charAt(0).toUpperCase()}
                                      </span>
                                    </div>
                                    <div>
                                      <p className="font-semibold text-on-surface text-sm">{comentario.usuario_nome}</p>
                                      <p className="text-xs text-secondary">{formatarDataHora(comentario.created_at)}</p>
                                    </div>
                                  </div>
                                  <p className="text-on-surface ml-10">{comentario.mensagem}</p>
                                </div>
                                {(comentario.usuario_id === user?.dbId || user?.role === 'admin' || user?.setor === 'RH') && (
                                  <button
                                    onClick={() => handleDeletarComentario(comentario.id, cpf)}
                                    className="text-error/50 hover:text-error ml-2 p-1 hover:bg-error-container/30 rounded transition-colors"
                                    title="Excluir mensagem"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <div className="w-16 h-16 bg-secondary-container rounded-full flex items-center justify-center mx-auto mb-4">
                            <MessageCircle className="w-8 h-8 text-secondary" />
                          </div>
                          <p className="text-on-surface mb-2">Ainda nao ha mensagens</p>
                          <p className="text-sm text-secondary">Seja o primeiro a enviar seus parabens!</p>
                        </div>
                      )}
                    </div>

                    <div className="p-6 bg-white dark:bg-gray-800 border-t border-outline-variant">
                      <div className="flex items-center space-x-2 mb-3">
                        <Sparkles className="w-4 h-4 text-primary" />
                        <span className="text-sm text-secondary">
                          {(() => {
                            const cpf = pessoaSelecionada.cpf || pessoaSelecionada.nome;
                            const comentarios = comentariosPorPessoa[cpf] || [];
                            const jaComentou = comentarios.some(c => c.usuario_id === user?.dbId);
                            return jaComentou
                              ? 'Envie mais mensagens de parabens!'
                              : 'Envie sua primeira mensagem e ganhe +2 pontos!';
                          })()}
                        </span>
                      </div>
                      <div className="flex space-x-3">
                        <textarea
                          value={novoComentario}
                          onChange={(e) => setNovoComentario(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleEnviarComentario();
                            }
                          }}
                          placeholder={`Envie seus parabens para ${pessoaSelecionada.nome.split(' ')[0]}...`}
                          className="flex-1 px-4 py-3 border border-outline-variant rounded-xl bg-surface-container-lowest text-on-surface focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none text-sm"
                          rows={2}
                          disabled={enviandoComentario}
                        />
                        <button
                          onClick={handleEnviarComentario}
                          disabled={!novoComentario.trim() || enviandoComentario}
                          className="px-6 py-3 rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 bg-primary hover:bg-primary-dark text-white shadow-sm active:scale-95"
                        >
                          <Send className="w-4 h-4" />
                          <span>{enviandoComentario ? '...' : 'Enviar'}</span>
                        </button>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};